import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search } from 'lucide-react';

const SearchBar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileSearchQuery, setMobileSearchQuery] = useState('');
  const [desktopSearchTerm, setDesktopSearchTerm] = useState('');
  const [showDesktopSearch, setShowDesktopSearch] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const touchStartY = useRef<number>(0);
  
  const desktopSearchRef = useRef<HTMLDivElement>(null);
  const mobileSearchRef = useRef<HTMLDivElement>(null);
  const desktopInputRef = useRef<HTMLInputElement>(null);
  const mobileInputRef = useRef<HTMLInputElement>(null);
  const lastInteractionTime = useRef<number>(0);

  // Auto-focus the appropriate search input when on the /search page
  useEffect(() => {
    if (location.pathname === '/search') {
      // Small timeout to ensure elements are mounted
      const timer = setTimeout(() => {
        if (isMobile) {
          if (mobileInputRef.current) {
            mobileInputRef.current.focus();
          }
        } else {
          if (desktopInputRef.current) {
            desktopInputRef.current.focus();
          }
        }
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [location.pathname, isMobile]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleInteraction = (event: Event) => {
      // Throttle the function to improve performance
      const now = Date.now();
      if (now - lastInteractionTime.current < 100) return;
      lastInteractionTime.current = now;

      // Handle click outside for both desktop and mobile
      if (event.type === 'mousedown' || event.type === 'touchstart') {
        const target = event.target as Node;
        
        if (desktopSearchRef.current && !desktopSearchRef.current.contains(target)) {
          setShowDesktopSearch(false);
        }
        if (mobileSearchRef.current && !mobileSearchRef.current.contains(target)) {
          setShowMobileSearch(false);
        }
      }
      
      // Handle scroll outside for both desktop and mobile
      if (event.type === 'scroll') {
        // Only run this if either dropdown is open
        if (!showDesktopSearch && !showMobileSearch) return;
        
        const target = event.target as Node;
        
        // Check if the scroll happened outside both search containers
        const isDesktopScroll = desktopSearchRef.current && desktopSearchRef.current.contains(target);
        const isMobileScroll = mobileSearchRef.current && mobileSearchRef.current.contains(target);
        
        if (showDesktopSearch && !isDesktopScroll) {
          setShowDesktopSearch(false);
        }
        if (showMobileSearch && !isMobileScroll) {
          setShowMobileSearch(false);
        }
      }
    };

    // Handle touch move to detect scrolling on mobile
    const handleTouchMove = (e: TouchEvent) => {
      if (!showDesktopSearch && !showMobileSearch) return;
      
      const touchY = e.touches[0].clientY;
      const touchDiff = Math.abs(touchY - touchStartY.current);
      
      // Only consider it a scroll if the touch moved significantly
      if (touchDiff > 10) {
        const target = e.target as Node;
        
        if (showDesktopSearch && desktopSearchRef.current && !desktopSearchRef.current.contains(target)) {
          setShowDesktopSearch(false);
        }
        if (showMobileSearch && mobileSearchRef.current && !mobileSearchRef.current.contains(target)) {
          setShowMobileSearch(false);
        }
      }
    };

    // Track touch start position
    const handleTouchStart = (e: TouchEvent) => {
      touchStartY.current = e.touches[0].clientY;
    };

    // Add event listeners
    document.addEventListener('mousedown', handleInteraction);
    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('scroll', handleInteraction, { passive: true, capture: true });
    
    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleInteraction);
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('scroll', handleInteraction, { capture: true } as any);
    };
  }, [showDesktopSearch, showMobileSearch]);

  // Handle form submission - navigate to search results page when Enter is pressed
  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Determine which search term to use based on device type
    const searchTerm = isMobile ? mobileSearchQuery : desktopSearchTerm;
    
    // Navigate to search results page if there's a search term
    if (searchTerm.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchTerm.trim())}`);
      // Close dropdowns after navigation
      setShowMobileSearch(false);
      setShowDesktopSearch(false);
    }
  };

  return (
    <>
      {/* Mobile Search */}
      <div className="search-bar md:hidden" ref={mobileSearchRef}>
        <form onSubmit={handleFormSubmit} className="relative" style={{ position: 'relative' }}>
          <div className="search-input-container" >
            <Search className="search-icon" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', zIndex: 10 }} />
            <input
              id="mobile-search-input"
              ref={mobileInputRef}
              type="text"
              placeholder="Search Products"
              value={mobileSearchQuery}
              onChange={(e) => {
                const value = e.target.value;
                setMobileSearchQuery(value);
                const term = value.trim();
                if (term) {
                  navigate(`/search?q=${encodeURIComponent(term)}`);
                } else {
                  navigate('/search');
                }
              }}
              onFocus={() => {
                // Only navigate when not already on the search results page
                if (location.pathname !== '/search') {
                  const term = mobileSearchQuery.trim();
                  if (term) {
                    navigate(`/search?q=${encodeURIComponent(term)}`);
                  } else {
                    navigate('/search');
                  }
                }
              }}
              autoFocus={location.pathname === '/search'}
              className="search-input"
            />

          </div>
        </form>
      </div>

      {/* Desktop Search */}
      <div className="hidden md:flex items-center">
        <div className="relative" ref={desktopSearchRef}>
          <form onSubmit={handleFormSubmit} className="relative flex">
            <input
              id="desktop-search-input"
              ref={desktopInputRef}
              type="text"
              placeholder="Search products..."
              value={desktopSearchTerm}
              onChange={(e) => {
                const value = e.target.value;
                setDesktopSearchTerm(value);
                const term = value.trim();
                if (term) {
                  navigate(`/search?q=${encodeURIComponent(term)}`);
                } else {
                  navigate('/search');
                }
              }}
              onFocus={() => {
                // Only navigate when not already on the search results page
                if (location.pathname !== '/search') {
                  const term = desktopSearchTerm.trim();
                  if (term) {
                    navigate(`/search?q=${encodeURIComponent(term)}`);
                  } else {
                    navigate('/search');
                  }
                }
              }}
              autoFocus={location.pathname === '/search'}
            />

            <button
              type="button"
              className="px-4 py-2 bg-brand-yellow hover:bg-brand-yellow-dark text-brand-charcoal rounded-r-md shadow transition-colors duration-200 flex items-center justify-center"
              onClick={() => {
                const term = desktopSearchTerm.trim();
                if (term) {
                  navigate(`/search?q=${encodeURIComponent(term)}`);
                } else {
                  navigate('/search');
                }
              }}
            >
              <Search className="h-4 w-4" />
            </button> 
          </form>
        </div>
      </div>

    </>
  );
};

export default SearchBar;